"use client";

/**
 * 找服务商品区：企业服务 + 单品资源统一为卡片样式，按一级 Tab（企业服务 / Token算力 / 通用AI应用）切换。
 * - 主推商品（Token Plan 企业版 / 个人版）额外展示能力亮点，选项横排、折扣与价格同一行以压缩高度。
 * - 其余商品以横向滑动（scroller）方式展示，超出 4 个可左右滑动，不再按行平铺。
 * - 规格下拉真交互（点击展开、选中切换），价格随选项联动展示；matrix 商品第一维切换时第二维重置。
 * - 立即购买：offer.url 存在时直跳该地址；否则回落到活动页（抽屉逻辑 purchaseCampaign 保留备用）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CATEGORIES,
  PRODUCTS,
  type Offer,
  type Product,
  type ProductCategory,
} from "@/common/portal/products.data";
import { purchaseCampaign } from "@/common/portal/portalOrder";
import { showToast } from "@/common/portal/toast";

// 立即购买目标活动页（无 offer.url 时的回落地址；抽屉逻辑 purchaseCampaign 保留备用）
const CAMPAIGN_URL = "https://cloud.baidu.com/campaign/event/ai/index.html";
// 保留订单抽屉能力引用，后续切回抽屉方式时直接调用（避免未使用告警）
void purchaseCampaign;
void showToast;

type Sel = { i: number; row: number; col: number };

function isMatrix(offers: Offer[] | Offer[][]): offers is Offer[][] {
  return Array.isArray(offers[0]);
}

/** 依据当前选择解析出生效的 offer。 */
function resolveOffer(p: Product, sel: Sel): Offer {
  if (isMatrix(p.offers)) {
    const group = p.offers[sel.row] || p.offers[0];
    return group[sel.col] || group[0];
  }
  return p.offers[sel.i] || p.offers[0];
}

/** 某个下拉维度可选项的文案列表（用于渲染面板）。 */
function optionsFor(p: Product, dim: "i" | "row" | "col", sel: Sel, bind: string): string[] {
  if (isMatrix(p.offers)) {
    if (dim === "row") return p.offers.map((g) => g[0][bind]);
    // col：取当前 row 下每个 offer 的文案
    const group = p.offers[sel.row] || p.offers[0];
    return group.map((o) => o[bind]);
  }
  return (p.offers as Offer[]).map((o) => o[bind]);
}

function ProductCard({ product }: { product: Product }) {
  const [sel, setSel] = useState<Sel>({ i: 0, row: 0, col: 0 });
  const [openDim, setOpenDim] = useState<string | null>(null);
  const cardRef = useRef<HTMLElement>(null);

  const offer = resolveOffer(product, sel);
  const hasDiscount = !!offer.discount;
  const showOrigin = !!offer.originPrice && offer.originPrice !== offer.price;
  // 价格拆分：数字主体大字号，「起」「起/年」「/次」等后缀走小字号（与折现价一致）
  const priceBody = offer.price.replace(/^¥/, "");
  const priceMatch = priceBody.match(/^([\d.,]+)(.*)$/);
  const priceNum = priceMatch ? priceMatch[1] : priceBody;
  const priceUnit = priceMatch ? priceMatch[2] : "";

  // 点击卡片外关闭下拉
  useEffect(() => {
    if (!openDim) return;
    const onDocClick = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setOpenDim(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openDim]);

  const pick = useCallback((dim: "i" | "row" | "col", index: number) => {
    setSel((prev) => {
      if (dim === "i") return { ...prev, i: index };
      if (dim === "row") return { ...prev, row: index, col: 0 }; // 第一维变化时第二维重置
      return { ...prev, col: index };
    });
    setOpenDim(null);
  }, []);

  const activeIndex = (dim: "i" | "row" | "col") => (dim === "i" ? sel.i : dim === "row" ? sel.row : sel.col);

  return (
    <article ref={cardRef} className={`opc-token-card${product.featured ? " is-featured" : ""}`}>
      <h3>{product.title}</h3>
      <p>{product.desc}</p>

      {product.featured && product.highlights?.length ? (
        <ul className="opc-token-highlights">
          {product.highlights.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      ) : null}

      <div className={`opc-token-config${product.rows.some((r) => !r.select && "wrap" in r && r.wrap) ? " is-single" : ""}`}>
        {product.rows.map((r) => {
          if (!r.select) {
            const wrap = "wrap" in r && r.wrap;
            return (
              <div
                className={`opc-token-config-row no-arrow${wrap ? " is-wrap" : ""}`}
                key={r.bind}
              >
                <span>{r.label}</span>
                <strong>{offer[r.bind]}</strong>
              </div>
            );
          }
          const opts = optionsFor(product, r.dim, sel, r.bind);
          const isOpen = openDim === r.dim;
          const active = activeIndex(r.dim);
          return (
            <div
              className={`opc-token-config-row opc-token-select-row${isOpen ? " is-open" : ""}`}
              key={r.bind}
            >
              <span>{r.label}</span>
              <strong
                role="button"
                tabIndex={0}
                onClick={() => setOpenDim(isOpen ? null : r.dim)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenDim(isOpen ? null : r.dim);
                  }
                }}
              >
                {offer[r.bind]}
              </strong>
              <div className="opc-token-select-panel">
                {opts.map((label, idx) => (
                  <button
                    type="button"
                    key={label + idx}
                    className={idx === active ? "is-active" : ""}
                    onClick={() => pick(r.dim, idx)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="opc-token-foot">
        <div className={`opc-token-discount${hasDiscount || offer.limit ? "" : " is-empty"}`}>
          {hasDiscount ? <b>{offer.discount}</b> : null}
          {offer.limit ? <span>{offer.limit}</span> : null}
        </div>

        <div className="opc-token-price">
          <strong>
            <small>¥</small>
            {priceNum}
          </strong>
          {priceUnit ? <small className="opc-token-price-unit">{priceUnit}</small> : null}
          {showOrigin ? <small>原价:</small> : null}
          {showOrigin ? <del>{offer.originPrice}</del> : null}
        </div>

        <div className="opc-token-actions">
          <button
            className="opc-btn"
            type="button"
            onClick={() => window.open(offer.url || CAMPAIGN_URL, "_blank", "noopener,noreferrer")}
          >
            立即购买
          </button>
        </div>
      </div>
    </article>
  );
}

// ---- toast 已抽到 @/common/portal/toast ----

/** 单张分类的商品渲染：主推卡（更大、带亮点）+ 其余横向滑动。 */
function CategoryPanel({ items }: { items: Product[] }) {
  const featured = items.filter((p) => p.featured);
  const rest = items.filter((p) => !p.featured);

  return (
    <>
      {featured.length ? (
        <div className="opc-token-grid featured-grid">
          {featured.map((p) => (
            <ProductCard key={p.key} product={p} />
          ))}
        </div>
      ) : null}
      {rest.length ? (
        <div className="opc-token-scroller">
          <div className="opc-token-track">
            {rest.map((p) => (
              <ProductCard key={p.key} product={p} />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** 找服务商品区：一级 Tab（企业服务 / Token算力 / 通用AI应用）切换展示对应商品卡。 */
export default function ProductMarket() {
  // 仅保留有商品的分类
  const tabs = useMemo(
    () => CATEGORIES.filter((c) => PRODUCTS.some((p) => p.category === c.key)),
    [],
  );
  const [active, setActive] = useState<ProductCategory>(tabs[0]?.key ?? "enterprise");

  return (
    <section className="opc-tokens">
      <div className="directory-major-tabs product-market-tabs">
        {tabs.map((c) => (
          <button
            key={c.key}
            type="button"
            className={active === c.key ? "active" : ""}
            onClick={() => setActive(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {tabs.map((c) => (
        <div key={c.key} style={{ display: active === c.key ? undefined : "none" }}>
          <CategoryPanel items={PRODUCTS.filter((p) => p.category === c.key)} />
        </div>
      ))}
    </section>
  );
}
